import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { CheckoutContentExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { sanitizeString } from '@pdqdev/pdq-shared/dist/utils/rich-text-utils';
import { getBlockImageSrc } from '@pages/checkout-app/checkout-layout/main/components/extensions/components/extension-components/checkout-content/checkout-content.utils';
import { TimerConfiguration } from '@pdqdev/pdq-shared/dist/interfaces';
import {
  TIMER_STATES,
  TIME_FORMATS,
  APPEARANCE_TYPES,
  CHECKOUT_CONTENT_TIMER_PLACEMENTS,
} from '@pdqdev/pdq-shared/dist/utils/extensions-timer-utils';

enum ElementType {
  PARAGRAPH = 'paragraph',
  HEADER = 'header',
}

interface TextElement {
  content: string;
  alignment: string;
  type?: ElementType;
  level?: number;
  hasCountdown?: boolean;
}

// CSS class constants
const CSS_CLASSES = {
  TEXT_ELEMENT: 'dynamic-block__text-element',
  TEXT_ALIGN_PREFIX: 'text-align-',
} as const;

@Component({
  selector: 'dynamic-block',
  templateUrl: './dynamic-block.component.html',
  styleUrls: ['./dynamic-block.component.scss'],
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class DynamicBlockComponent implements OnInit, OnChanges {
  @Input() block: CheckoutContentExtension.DynamicBlock;
  @Input() template: CheckoutContentExtension.Template;
  @Input() timerConfiguration: TimerConfiguration;

  shrinkImageContainer = false;
  textElements: TextElement[] = [];

  get isPerRowTimerEnabled(): boolean {
    return (
      this.timerConfiguration?.enableCountdownTimer &&
      this.timerConfiguration?.placement === CHECKOUT_CONTENT_TIMER_PLACEMENTS.PER_ROW
    );
  }

  get combinedTextHtml(): string {
    if (!this.isPerRowTimerEnabled) return '';
    return this.textElements.map((el) => this.getElementHtml(el)).join('');
  }

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.template) {
      this.updateShrinkImageContainer();
    }
    if (changes.block) {
      this.initTextElements();
    }
    if (changes.timerConfiguration) {
      this.initTextElements();
    }
  }

  ngOnInit(): void {
    this.updateShrinkImageContainer();
    this.initTextElements();
  }

  private updateShrinkImageContainer() {
    if (!this.template?.blocks) return;
    const dynamicBlocks = this.template.blocks.filter(
      (block) => block.type === CheckoutContentExtension.BlockType.DYNAMIC,
    ) as CheckoutContentExtension.DynamicBlock[];
    this.shrinkImageContainer = dynamicBlocks.every((block) => !getBlockImageSrc(block));
  }

  private initTextElements() {
    if (!this.block?.bodyText) {
      this.textElements = [];
      return;
    }

    this.textElements = this.processHtmlContent(this.block.bodyText);
  }

  private processHtmlContent(bodyText: string): TextElement[] {
    const sanitized = sanitizeString(bodyText, {
      noLineBreaks: false,
      removePunctuation: true,
    });

    // Split by both paragraph and header closing tags
    const elements = sanitized
      .split(/(<\/p>|<\/h[1-4]>)/i)
      .filter((element) => element.trim() && !element.match(/^<\/(p|h[1-4])>$/i));

    const textElements: TextElement[] = [];

    elements.forEach((element) => {
      const trimmedElement = element.trim();

      const headerMatch = trimmedElement.match(/^<h([1-4])[^>]*>(.*)/i);
      if (headerMatch) {
        const level = parseInt(headerMatch[1], 10);
        const alignment = this.extractAlignment(trimmedElement);
        // Extract content but keep inner HTML formatting
        const content = headerMatch[2].trim();

        if (content) {
          textElements.push({
            content,
            alignment,
            type: ElementType.HEADER,
            level,
            hasCountdown: content.includes('{countdown}'),
          });
        }
        return;
      }

      // Handle paragraphs (existing logic)
      let alignment = 'left';
      const alignmentMatch = trimmedElement.match(/ql-align-(\w+)/);
      if (alignmentMatch && alignmentMatch[1]) {
        alignment = alignmentMatch[1];
      }

      const content = trimmedElement.replace(/<p[^>]*>/i, '').trim();
      if (content === '' || content === '<br>') {
        textElements.push({ content: '', alignment, type: ElementType.PARAGRAPH, hasCountdown: false });
      } else {
        const lines = content.split(/<br\s*\/?>/i);
        for (const line of lines) {
          textElements.push({
            content: line ? line.trim() : '',
            alignment,
            type: ElementType.PARAGRAPH,
            hasCountdown: line.includes('{countdown}'),
          });
        }
      }
    });

    return textElements;
  }

  private extractAlignment(html: string): string {
    const alignmentMatch = html.match(/ql-align-(\w+)/);
    return alignmentMatch && alignmentMatch[1] ? alignmentMatch[1] : 'left';
  }

  getElementHtml(element: TextElement): string {
    let content = element.content || '&nbsp;';

    const hasTimer = element.hasCountdown && this.shouldRenderPerRowTimer();
    if (hasTimer) {
      content = this.replaceCountdownToken(content);
    }

    const alignmentClass = `${CSS_CLASSES.TEXT_ALIGN_PREFIX}${element.alignment}`;
    const useInlineRendering = this.shouldRenderPerRowTimer();
    const inlineClass = useInlineRendering ? 'timer-inline' : '';
    const classes = `${CSS_CLASSES.TEXT_ELEMENT} ${alignmentClass} ${inlineClass}`.trim();

    if (element.type === ElementType.HEADER && element.level) {
      return `<h${element.level} class="${classes}">${content}</h${element.level}>`;
    } else if (useInlineRendering) {
      // Use span for ALL elements when per-row timer is enabled to make them flow inline
      return `<span class="${classes}">${content} </span>`;
    } else {
      return `<p class="${classes}">${content}</p>`;
    }
  }

  private shouldRenderPerRowTimer(): boolean {
    return this.timerConfiguration?.enableCountdownTimer && this.timerConfiguration?.placement === 'per-row';
  }

  private replaceCountdownToken(content: string): string {
    const countdown = this.getExampleCountdown();
    const timerClass = this.getTimerClass();
    return content.replace(/{countdown}/g, `<span class="timer-countdown ${timerClass}">${countdown}</span>`);
  }

  private getTimerMessage(): string {
    const state = this.timerConfiguration?.timerPreviewMode || TIMER_STATES.DEFAULT;

    if (state === TIMER_STATES.EXPIRED) {
      return this.timerConfiguration?.expiredMessage || 'offer expired';
    } else if (state === TIMER_STATES.LOW) {
      return this.timerConfiguration?.lowTimerMessage || 'Hurry - only {countdown} left!';
    } else {
      return this.timerConfiguration?.defaultMessage || 'Only {countdown} left to claim this offer!';
    }
  }

  private getExampleCountdown(): string {
    const format = this.timerConfiguration?.timeFormat || TIME_FORMATS.DIGITAL;
    return format === TIME_FORMATS.DIGITAL ? '04:30' : '4m 30s';
  }

  private getTimerClass(): string {
    const state = this.timerConfiguration?.timerPreviewMode || TIMER_STATES.DEFAULT;
    let bgColor: string;

    if (state === TIMER_STATES.EXPIRED) {
      bgColor = this.timerConfiguration?.expiredBackgroundColor || APPEARANCE_TYPES.CRITICAL;
    } else if (state === TIMER_STATES.LOW) {
      bgColor = this.timerConfiguration?.lowTimerBackgroundColor || APPEARANCE_TYPES.WARNING;
    } else {
      bgColor = this.timerConfiguration?.backgroundColor || APPEARANCE_TYPES.INFO;
    }

    return `timer-${bgColor}`;
  }

  protected readonly CheckoutContentExtension = CheckoutContentExtension;
}
